import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api } from "@/lib/api";
import { Button, ErrorNote, Field, Input, Modal } from "./ui";

const MIN = 12;

/** Change one's own password. `forced`: after a temporary password, can't be dismissed. */
export function PasswordDialog({ forced, onClose }: { forced?: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mismatch = again.length > 0 && next !== again;
  const ready = current.length > 0 && next.length >= MIN && next === again;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await api("/auth/password", {
        method: "POST",
        body: { current_password: current, new_password: next },
      });
      await qc.invalidateQueries({ queryKey: ["me"] });
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && !forced && onClose()}
      title={forced ? "Choose your password" : "Change password"}
      description={
        forced
          ? "You signed in with a temporary password. Choose your own to continue."
          : `At least ${MIN} characters.`
      }
      footer={
        <>
          {!forced && (
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button type="submit" form="password-form" loading={busy} disabled={!ready}>
            Save password
          </Button>
        </>
      }
    >
      <form id="password-form" onSubmit={submit} className="space-y-5">
        <Field label={forced ? "Temporary password" : "Current password"}>
          <Input
            type="password"
            autoComplete="current-password"
            autoFocus
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field label="New password" hint={`At least ${MIN} characters`}>
          <Input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <Field label="New password again" hint={mismatch ? "The passwords differ" : undefined}>
          <Input
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
        </Field>
        <ErrorNote error={error} />
      </form>
    </Modal>
  );
}
